/**
 * Permissions Matrix Component
 * ISO 27001 A.9.2.3 - Management of privileged access rights
 *
 * Affiche la matrice des permissions par rôle pour référence
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Shield,
  Check,
  X,
  FileText,
  Receipt,
  Users,
  Package,
  Calculator,
  Settings,
  CreditCard,
} from 'lucide-react';
import { PERMISSION_MATRIX, CompanyAction, CompanyRole } from '@/hooks/usePermission';

const ROLE_KEYS: Record<CompanyRole, { key: string; color: string }> = {
  OWNER: {
    key: 'OWNER',
    color: 'bg-purple-100 text-purple-700',
  },
  ADMIN: {
    key: 'ADMIN',
    color: 'bg-blue-100 text-blue-700',
  },
  MANAGER: {
    key: 'MANAGER',
    color: 'bg-teal-100 text-teal-700',
  },
  ACCOUNTANT: {
    key: 'ACCOUNTANT',
    color: 'bg-amber-100 text-amber-700',
  },
  MEMBER: {
    key: 'MEMBER',
    color: 'bg-gray-100 text-gray-700',
  },
  VIEWER: {
    key: 'VIEWER',
    color: 'bg-gray-50 text-gray-600',
  },
};

const ACTION_CATEGORY_KEYS: { categoryKey: string; icon: React.ElementType; actions: CompanyAction[] }[] = [
  {
    categoryKey: 'invoices',
    icon: FileText,
    actions: ['create:invoice', 'read:invoice', 'update:invoice', 'delete:invoice'],
  },
  {
    categoryKey: 'quotes',
    icon: FileText,
    actions: ['create:quote', 'read:quote', 'update:quote', 'delete:quote'],
  },
  {
    categoryKey: 'creditNotes',
    icon: CreditCard,
    actions: ['create:credit-note', 'read:credit-note', 'update:credit-note', 'delete:credit-note'],
  },
  {
    categoryKey: 'clients',
    icon: Users,
    actions: ['create:client', 'read:client', 'update:client', 'delete:client'],
  },
  {
    categoryKey: 'products',
    icon: Package,
    actions: ['create:product', 'read:product', 'update:product', 'delete:product'],
  },
  {
    categoryKey: 'expenses',
    icon: Receipt,
    actions: ['create:expense', 'read:expense', 'update:expense', 'delete:expense', 'approve:expense'],
  },
  {
    categoryKey: 'accounting',
    icon: Calculator,
    actions: ['read:accounting', 'export:accounting', 'manage:vat', 'read:treasury'],
  },
  {
    categoryKey: 'administration',
    icon: Settings,
    actions: ['manage:settings', 'manage:users', 'manage:company'],
  },
];

const ACTION_LABEL_KEYS: Record<CompanyAction, string> = {
  'create:invoice': 'create',
  'read:invoice': 'read',
  'update:invoice': 'update',
  'delete:invoice': 'delete',
  'create:quote': 'create',
  'read:quote': 'read',
  'update:quote': 'update',
  'delete:quote': 'delete',
  'create:credit-note': 'create',
  'read:credit-note': 'read',
  'update:credit-note': 'update',
  'delete:credit-note': 'delete',
  'create:client': 'create',
  'read:client': 'read',
  'update:client': 'update',
  'delete:client': 'delete',
  'create:product': 'create',
  'read:product': 'read',
  'update:product': 'update',
  'delete:product': 'delete',
  'create:expense': 'create',
  'read:expense': 'read',
  'update:expense': 'update',
  'delete:expense': 'delete',
  'approve:expense': 'approve',
  'read:accounting': 'readAccounting',
  'export:accounting': 'exportAccounting',
  'manage:vat': 'manageVat',
  'read:treasury': 'readTreasury',
  'manage:settings': 'manageSettings',
  'manage:users': 'manageUsers',
  'manage:company': 'manageCompany',
};

export const PermissionsMatrix: React.FC = () => {
  const { t } = useTranslation();
  const roles = Object.keys(PERMISSION_MATRIX) as CompanyRole[];

  const hasPermission = (role: CompanyRole, action: CompanyAction): boolean => {
    return PERMISSION_MATRIX[role]?.includes(action) || false;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="p-3 bg-teal-100 rounded-2xl">
          <Shield className="w-8 h-8 text-teal-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">{t('admin.permissions.title')}</h2>
          <p className="text-gray-500">{t('admin.permissions.subtitle')}</p>
        </div>
      </div>

      {/* Roles Legend */}
      <div className="bg-white/70 backdrop-blur-xl rounded-3xl border border-white/40 p-6">
        <h3 className="font-semibold text-gray-900 mb-4">{t('admin.permissions.availableRoles')}</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {roles.map((role) => {
            const { key, color } = ROLE_KEYS[role];
            return (
              <div
                key={role}
                className={`p-3 rounded-xl ${color}`}
              >
                <p className="font-semibold">{t(`admin.permissions.roles.${key}.label`)}</p>
                <p className="text-sm opacity-80">{t(`admin.permissions.roles.${key}.description`)}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Permissions Grid */}
      <div className="bg-white/70 backdrop-blur-xl rounded-3xl border border-white/40 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50/50">
                <th className="text-left p-4 font-semibold text-gray-700">{t('admin.permissions.permissionColumn')}</th>
                {roles.map((role) => (
                  <th key={role} className="p-4 text-center">
                    <span className={`px-2 py-1 rounded-lg text-xs font-semibold ${ROLE_KEYS[role].color}`}>
                      {t(`admin.permissions.roles.${ROLE_KEYS[role].key}.label`)}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ACTION_CATEGORY_KEYS.map((category) => (
                <React.Fragment key={category.categoryKey}>
                  {/* Category Header */}
                  <tr className="bg-gray-100/50">
                    <td colSpan={roles.length + 1} className="p-3">
                      <div className="flex items-center gap-2 text-gray-700 font-semibold">
                        <category.icon className="w-4 h-4" />
                        {t(`admin.permissions.categories.${category.categoryKey}`)}
                      </div>
                    </td>
                  </tr>
                  {/* Actions */}
                  {category.actions.map((action) => (
                    <tr key={action} className="border-b border-gray-100 last:border-0">
                      <td className="p-3 pl-8 text-gray-600 text-sm">
                        {t(`admin.permissions.actions.${ACTION_LABEL_KEYS[action]}`)}
                      </td>
                      {roles.map((role) => (
                        <td key={`${role}-${action}`} className="p-3 text-center">
                          {hasPermission(role, action) ? (
                            <Check className="w-5 h-5 text-green-600 mx-auto" />
                          ) : (
                            <X className="w-5 h-5 text-gray-300 mx-auto" />
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Notes */}
      <div className="bg-amber-50/50 backdrop-blur-xl rounded-2xl border border-amber-200/50 p-4">
        <p className="text-sm text-amber-800">
          <strong>{t('admin.permissions.noteLabel')}</strong> {t('admin.permissions.note')}
        </p>
      </div>
    </div>
  );
};

export default PermissionsMatrix;
